import { useMemo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Cookies from 'universal-cookie';
import { usePreferences } from '@context/preferences.context';
import { useDeviceSize } from '@context/device-size.context';
import { isPricingCalculatorPage, isWebview } from '@helpers/routing';
import { getRegionIdByRegionName } from '@helpers/localization';
import { DeviceType } from '@enums/deviceType';
import Environment from '@config/index';
import { BANNER_SIZE, BANNER_CONFIG, BannerSize, BannerVisibilityState, BannerState } from '@constants/smart-banner-v2';

const cookies = new Cookies();

export const useSmartBannerVisibilityManager = () => {
  const { siteId } = usePreferences();
  const { pathname, query } = useRouter();
  const isPCPage = isPricingCalculatorPage(pathname);
  const { deviceType } = useDeviceSize();
  const isWebView = isWebview(query);
  const [isClient, setIsClient] = useState(false);
  const [isBannerClosed, setIsBannerClosed] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClient(true);
    }
  }, []);

  const shouldShowBanner = useMemo(() => {
    if (!isClient) {
      return false;
    }
    const isKr = siteId === getRegionIdByRegionName('KR');
    const isNaverTraffic = query.utm_medium === 'naver_shopping' && query.utm_source === 'naver';
    // Hide smart banner on Pricing Calculator mWeb (isPCpage)
    const hideSmartBanner = (isKr && isNaverTraffic) || isPCPage || isWebView;

    // Should hide smart banner for KR site when traffic is from Naver and FF is enabled
    if (hideSmartBanner) {
      return false;
    }
    return !(deviceType === DeviceType.DESKTOP);
  }, [isClient]);

  const getBannerState = (size: BannerSize): BannerState => {
    const cookieValue = cookies.get(BANNER_CONFIG.COOKIES[size]);
    if (!cookieValue) {
      return { isClosed: false, closedAt: null };
    }
    return {
      isClosed: true,
      closedAt: new Date(cookieValue).getTime(),
    };
  };

  const visibilityState = useMemo((): BannerVisibilityState => {
    if (!shouldShowBanner || typeof window === 'undefined' || isBannerClosed) {
      return { show: false, variant: null };
    }

    const bigBannerState = getBannerState(BANNER_SIZE.BIG);
    const smallBannerState = getBannerState(BANNER_SIZE.SMALL);

    // Show big banner if it's never been closed
    if (!bigBannerState.isClosed) {
      return { show: true, variant: BANNER_SIZE.BIG };
    }

    // Show small banner if big banner cooldown passed and small banner not closed or expired
    const timeSinceBigBannerClosed = Date.now() - (bigBannerState.closedAt || 0);
    const timeSinceSmallBannerClosed = smallBannerState.closedAt ? Date.now() - smallBannerState.closedAt : 0;

    if (
      timeSinceBigBannerClosed >= BANNER_CONFIG.TIMINGS[BANNER_SIZE.BIG].cooldown &&
      (!smallBannerState.isClosed || timeSinceSmallBannerClosed >= BANNER_CONFIG.TIMINGS[BANNER_SIZE.SMALL].expiry)
    ) {
      return { show: true, variant: BANNER_SIZE.SMALL };
    }

    return { show: false, variant: null };
  }, [shouldShowBanner, isBannerClosed]);

  const closeBanner = (): void => {
    const size = visibilityState.variant;
    const now = new Date().toISOString();
    const cookieName = BANNER_CONFIG.COOKIES[size];
    const expiryDate =
      size === BANNER_SIZE.BIG
        ? new Date('2100-01-01')
        : new Date(Date.now() + BANNER_CONFIG.TIMINGS[BANNER_SIZE.SMALL].expiry);

    cookies.set(cookieName, now, {
      path: '/',
      domain: Environment.cookieOptions.domain,
      sameSite: 'lax' as const,
      expires: expiryDate,
    });

    setIsBannerClosed(true);
  };

  return {
    visibilityState,
    handleSmartBannerClose: closeBanner,
    shouldDisplaySmallSmallBanner: visibilityState.show && visibilityState.variant === BANNER_SIZE.SMALL,
  };
};
